import {
  HHChip,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, IconButton, Paper } from '@mui/material';
import { useState } from 'react';
import { ContractTree, TreeNode } from '../../utils/build-contract-tree';
import { EntryBadge } from '../../utils/build-section-summaries';

const monoText = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 12,
};

type ChipColor = 'success' | 'info' | 'error' | 'warning' | 'default';

const BADGE_COLOR: Record<EntryBadge, ChipColor> = {
  reuse: 'success',
  new: 'info',
  committed: 'success',
  failed: 'error',
  pending: 'default',
  skipped: 'warning',
  skip: 'default',
};

const BADGE_LABEL: Record<EntryBadge, string> = {
  reuse: 'Reuse',
  new: 'Will create',
  committed: 'Created',
  failed: 'Failed',
  pending: 'Pending',
  skipped: 'Skipped',
  skip: 'Skip create',
};

const BORDER_COLOR: Record<EntryBadge, string> = {
  reuse: '#198754',
  new: '#0d6efd',
  committed: '#198754',
  failed: '#dc3545',
  pending: 'rgba(0,0,0,0.2)',
  skipped: '#ffc107',
  skip: 'rgba(0,0,0,0.2)',
};

const INDENT_PX = 20;

interface TreeRowProps {
  node: TreeNode;
  level: number;
}

const TreeRow = ({ node, level }: TreeRowProps): JSX.Element => {
  const [expanded, setExpanded] = useState<boolean>(
    node.defaultExpanded ?? level <= 1,
  );
  const hasChildren = node.children != null && node.children.length > 0;
  const borderColor = node.badge != null ? BORDER_COLOR[node.badge] : undefined;

  return (
    <Box>
      <HHStack
        direction="row"
        spacing={0.5}
        alignItems="flex-start"
        sx={{
          pl: `${level * INDENT_PX}px`,
          pr: 1,
          py: 0.5,
          borderLeft:
            borderColor != null ? `3px solid ${borderColor}` : undefined,
          ml: borderColor != null ? `${level * INDENT_PX}px` : undefined,
          // When a row has a badge, we use the left border for status. The
          // outer indent is shifted into ml so the border lines up under the
          // node label, not at column 0 of the page.
          ...(borderColor != null ? { pl: 1 } : {}),
        }}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={(): void => setExpanded(e => !e)}
            sx={{ p: 0.25, mt: '-2px' }}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </IconButton>
        ) : (
          <Box sx={{ width: 24, flexShrink: 0 }} />
        )}
        <HHStack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
          <HHStack
            direction="row"
            spacing={1.25}
            alignItems="baseline"
            sx={{ flexWrap: 'wrap' }}
          >
            <HHTypography hhVariant="body2" sx={{ fontWeight: 500 }}>
              {node.label}
            </HHTypography>
            {node.badge != null && (
              <HHChip
                hhVariant="variant-bypass"
                size="small"
                color={BADGE_COLOR[node.badge]}
                label={BADGE_LABEL[node.badge]}
              />
            )}
            {node.entityId != null && (
              <HHTypography
                hhVariant="caption"
                sx={{ ...monoText, opacity: 0.65, wordBreak: 'break-all' }}
              >
                id: {node.entityId}
              </HHTypography>
            )}
          </HHStack>
          {node.caption != null && (
            <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
              {node.caption}
            </HHTypography>
          )}
          {node.details != null && node.details.length > 0 && (
            <HHStack spacing={0.25} sx={{ pt: 0.25 }}>
              {node.details.map(d => (
                <HHStack
                  key={`${d.label}:${d.value}`}
                  direction="row"
                  spacing={2}
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  <HHTypography
                    hhVariant="caption"
                    sx={{ width: 140, opacity: 0.6 }}
                  >
                    {d.label}
                  </HHTypography>
                  <HHTypography hhVariant="caption" sx={monoText}>
                    {d.value}
                  </HHTypography>
                </HHStack>
              ))}
            </HHStack>
          )}
          {node.error != null && (
            <HHTypography
              hhVariant="caption"
              sx={{ color: BORDER_COLOR.failed, ...monoText }}
            >
              {node.error}
            </HHTypography>
          )}
        </HHStack>
      </HHStack>
      {hasChildren && (
        <Collapse in={expanded} unmountOnExit>
          <Box>
            {(node.children ?? []).map(child => (
              <TreeRow key={child.id} node={child} level={level + 1} />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

interface TreeBranchProps {
  title: string;
  caption?: string;
  nodes: TreeNode[];
  emptyText?: string;
}

const TreeBranch = ({
  title,
  caption,
  nodes,
  emptyText,
}: TreeBranchProps): JSX.Element => (
  <Paper variant="outlined" sx={{ p: 1.5 }}>
    <HHStack spacing={0.25} sx={{ pb: 1 }}>
      <HHTypography hhVariant="subtitle2">{title}</HHTypography>
      {caption != null && (
        <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
          {caption}
        </HHTypography>
      )}
    </HHStack>
    {nodes.length === 0 ? (
      <HHTypography hhVariant="caption" sx={{ opacity: 0.6, pl: 1 }}>
        {emptyText ?? 'Nothing to show.'}
      </HHTypography>
    ) : (
      <Box>
        {nodes.map(node => (
          <TreeRow key={node.id} node={node} level={0} />
        ))}
      </Box>
    )}
  </Paper>
);

interface ContractTreeViewProps {
  tree: ContractTree;
}

export const ContractTreeView = ({
  tree,
}: ContractTreeViewProps): JSX.Element => (
  <HHStack spacing={2}>
    <TreeBranch
      title={
        tree.contracts.length > 1
          ? `Contracts (${tree.contracts.length})`
          : 'Contract'
      }
      caption="One subtree per legal entity. Customer Account, Merchant + Locations + Practitioners, and the contract itself all roll up here."
      nodes={tree.contracts}
      emptyText="Contract decision not yet made."
    />
    <TreeBranch
      title="Shared resources"
      caption="Items, bundle, fee schedule, and CSPs — not scoped to a single TIN."
      nodes={tree.shared}
      emptyText="No shared resources configured."
    />
  </HHStack>
);
